import { Flex, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const { Option } = Select;

/**
 * ExtraFilters component.
 * @component
 *
 * Shared component for filtering by city and department.
 * @param {Object} props - Component props.
 * @param {Array<string>} props.cityOptions - Array of city options.
 * @param {Array<string>} props.departmentOptions - Array of department options.
 * @param {Function} props.setCityFilter - Function to set city filter.
 * @param {Function} props.setDepartmentFilter - Function to set department filter.
 * @param {boolean} props.isFieldsLoading - Loading state for fields.
 * @param {string} props.translationKey - Translation key prefix (e.g., 'pensioners' or 'owners').
 * @returns {ReactElement} The ExtraFilters component.
 */
export const ExtraFilters = ({
  cityOptions,
  departmentOptions,
  setCityFilter,
  setDepartmentFilter,
  isFieldsLoading,
  translationKey
}) => {
  const { t } = useTranslation();

  return (
    <Flex align="center" gap="middle" wrap="wrap">
      <Flex align="center" gap="small">
        {`${t(`${translationKey}.form.address.city`)} :`}
        <Select
          loading={isFieldsLoading}
          showSearch
          allowClear
          style={{ width: 200 }}
          onChange={(value) => setCityFilter(value)}
        >
          {cityOptions.map((city) => (
            <Option key={city} value={city}>
              {city}
            </Option>
          ))}
        </Select>
      </Flex>
      <Flex align="center" gap="small">
        {`${t(`${translationKey}.form.department`)} :`}
        <Select
          loading={isFieldsLoading}
          showSearch
          allowClear
          style={{ width: 200 }}
          onChange={(value) => setDepartmentFilter(value)}
        >
          {departmentOptions.map((department) => (
            <Option key={department} value={department}>
              {department}
            </Option>
          ))}
        </Select>
      </Flex>
    </Flex>
  );
};

ExtraFilters.propTypes = {
  departmentOptions: PropTypes.arrayOf(PropTypes.string),
  cityOptions: PropTypes.arrayOf(PropTypes.string),
  setCityFilter: PropTypes.func,
  setDepartmentFilter: PropTypes.func,
  isFieldsLoading: PropTypes.bool,
  translationKey: PropTypes.string.isRequired
};

ExtraFilters.defaultProps = {
  departmentOptions: null,
  cityOptions: null,
  setCityFilter: null,
  setDepartmentFilter: null,
  isFieldsLoading: false
};
